// Pick the thing a listing actually sells.
//
// A listing without an attached asset is just a price with a name on it, so
// this is the step that makes the marketplace real. Four sources, because those
// are the four places a creator's work already lives — asking them to re-upload
// something they have already uploaded is the kind of friction that stops people
// listing at all:
//
//   Worlds   — sold as a copyable world, referenced by id rather than file
//   Content  — anything in their upload library (user_uploads)
//   Avatars  — wearables and characters they generated
//   Upload   — something new, straight to storage
//
// Thumbnails come along automatically wherever the source already has one,
// which is most of the time. That matters: a storefront of unlabelled grey
// boxes is worse than no storefront, and nobody sets a thumbnail by hand if the
// flow lets them skip it.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Globe, Loader, Shirt, Upload, X } from 'lucide-react';
import { supabase } from '../config/supabase';
import { putObject } from '../utils/uploadToStorage';
import './AssetPicker.css';

export interface PickedAsset {
  /** What the buyer receives. Empty for worlds, which are referenced by id. */
  assetUrl: string | null;
  worldId: string | null;
  /** Which record moves when an ORIGINAL sells. A URL cannot be transferred. */
  sourceKind: 'world' | 'avatar' | 'upload' | null;
  sourceId: string | null;
  thumbnailUrl: string | null;
  /** Suggested title — the creator can still override it. */
  title: string;
  kind: 'model' | 'world' | 'prefab' | 'texture' | 'avatar';
}

type Tab = 'worlds' | 'content' | 'avatars' | 'upload';

interface Row {
  id: string;
  title: string;
  thumb: string | null;
  url: string | null;
  worldId: string | null;
  sourceKind: PickedAsset['sourceKind'];
  kind: PickedAsset['kind'];
  sub?: string;
}

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'worlds', label: 'Worlds', icon: <Globe size={14} /> },
  { id: 'content', label: 'Content', icon: <Box size={14} /> },
  { id: 'avatars', label: 'Avatars', icon: <Shirt size={14} /> },
  { id: 'upload', label: 'Upload', icon: <Upload size={14} /> },
];

export const AssetPicker: React.FC<{
  onPick: (asset: PickedAsset) => void;
  onClose: () => void;
}> = ({ onPick, onClose }) => {
  const [tab, setTab] = useState<Tab>('worlds');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (which: Tab) => {
    if (which === 'upload') { setRows([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setError('Sign in first.'); setLoading(false); return; }

      if (which === 'worlds') {
        const { data } = await supabase
          .from('worlds')
          .select('id, name, thumbnail_url')
          .eq('owner_id', user.id)
          .order('created_at', { ascending: false });
        setRows((data || []).map((w: any) => ({
          id: w.id,
          title: w.name || 'Untitled world',
          thumb: w.thumbnail_url || null,
          url: null,
          worldId: w.id,
          sourceKind: 'world',
          kind: 'world',
          sub: 'World',
        })));
      } else if (which === 'content') {
        const { data } = await supabase
          .from('user_uploads')
          .select('id, name, url, thumbnail_url, mime_type, metadata')
          .eq('user_id', user.id)
          .eq('type', 'content')
          .order('created_at', { ascending: false });
        setRows((data || []).map((u: any) => ({
          id: u.id,
          title: u.name || 'Untitled',
          thumb: u.thumbnail_url || u.metadata?.thumbnailUrl || u.metadata?.thumbnail_url || null,
          url: u.url,
          worldId: null,
          sourceKind: 'upload',
          kind: 'model',
          sub: (u.url?.split('.').pop() || '').toUpperCase(),
        })));
      } else {
        const { data } = await supabase
          .from('avatars')
          .select('id, name, model_url, thumbnail_url')
          .order('created_at', { ascending: false });
        setRows((data || []).map((a: any) => ({
          id: a.id,
          title: a.name || 'Avatar',
          thumb: a.thumbnail_url || null,
          url: a.model_url,
          worldId: null,
          sourceKind: 'avatar',
          kind: 'avatar',
          sub: 'Avatar',
        })));
      }
    } catch (e: any) {
      setError(e?.message || 'Could not load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const upload = async (file: File) => {
    setUploading(file.name);
    setError('');
    try {
      const key = `marketplace/${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
      const url = await putObject(key, file, file.type || 'application/octet-stream');
      // An uploaded image is its own thumbnail. Anything else has none until
      // the creator adds one.
      const isImage = (file.type || '').startsWith('image/');
      onPick({
        assetUrl: url,
        worldId: null,
        // A file uploaded straight to storage has no library row to transfer,
        // so it can only be sold as a copy. The listing form enforces this.
        sourceKind: null,
        sourceId: null,
        thumbnailUrl: isImage ? url : null,
        title: file.name.replace(/\.[^.]+$/, ''),
        kind: isImage ? 'texture' : 'model',
      });
    } catch (e: any) {
      setError(e?.message || 'Upload failed.');
    } finally {
      setUploading('');
    }
  };

  return createPortal(
    <div className="ap-scrim" onClick={onClose}>
      <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
        <header className="ap-head">
          <h2>What are you selling?</h2>
          <button className="ap-x" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </header>

        <div className="ap-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={`ap-tab ${tab === t.id ? 'on' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        <div className="ap-body">
          {tab === 'upload' ? (
            <div className="ap-upload">
              <input
                ref={fileRef}
                type="file"
                hidden
                accept=".glb,.gltf,.png,.jpg,.jpeg,.webp,.zip"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload(f);
                }}
              />
              <Upload size={26} />
              <h3>Upload a file</h3>
              <p>GLB or GLTF models, images, or a zip. It uploads straight to your storage.</p>
              <button
                className="ap-primary"
                onClick={() => fileRef.current?.click()}
                disabled={!!uploading}
              >
                {uploading ? <><Loader size={14} className="spin" /> {uploading}</> : 'Choose file'}
              </button>
            </div>
          ) : loading ? (
            <div className="ap-state"><Loader size={16} className="spin" /> Loading…</div>
          ) : !rows.length ? (
            <div className="ap-state">
              Nothing here yet. Try another tab, or upload something new.
            </div>
          ) : (
            <div className="ap-grid">
              {rows.map((r) => (
                <button
                  key={r.id}
                  className="ap-item"
                  onClick={() => onPick({
                    assetUrl: r.url,
                    worldId: r.worldId,
                    sourceKind: r.sourceKind,
                    sourceId: r.id,
                    thumbnailUrl: r.thumb,
                    title: r.title,
                    kind: r.kind,
                  })}
                >
                  <span className="ap-thumb">
                    {r.thumb
                      ? <img src={r.thumb} alt="" loading="lazy" />
                      : <Box size={18} />}
                  </span>
                  <span className="ap-name">{r.title}</span>
                  {r.sub && <span className="ap-sub">{r.sub}</span>}
                </button>
              ))}
            </div>
          )}

          {error && <p className="ap-error">{error}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AssetPicker;
